var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getDriveDailyTimelineLegendKey, getDriveTimelineItemActionLabel, getDriveTimelineItemUserLabel } from '@web/components/ItemDetails/DriveItemDetails/driveDailyTimelineUtils';
import Button from '@web/components/layout/Button';
import Table from '@web/components/layout/Table/Table';
import UserAvatar from '@web/components/UserAvatar';
import { DRIVE_DAILY_TIMELINE_COLORS_CONFIG, PAGINATION_PAGE_SIZE } from '@web/config';
import { classNames } from '@web/utility';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
export var DriveDocumentEventsColorSwatch = function (_a) {
    var legendKey = _a.legendKey, className = _a.className;
    return (_jsx("span", { className: classNames(className, 'inline-block shrink-0 rounded', "bg-".concat(DRIVE_DAILY_TIMELINE_COLORS_CONFIG[legendKey].color)), "aria-hidden": true }));
};
export var DriveDocumentEventsTable = function (_a) {
    var items = _a.items, page = _a.page, onPageChange = _a.onPageChange, _b = _a.wrapperClassName, wrapperClassName = _b === void 0 ? 'mt-6' : _b, _c = _a.variant, variant = _c === void 0 ? 'plain' : _c;
    var t = useTranslation().t;
    var pageSize = PAGINATION_PAGE_SIZE;
    var total = items.length;
    var totalPages = Math.max(1, Math.ceil(total / pageSize));
    var currentPage = Math.min(Math.max(page, 1), totalPages);
    var startIndex = (currentPage - 1) * pageSize;
    var paginatedItems = items.slice(startIndex, startIndex + pageSize);
    var displayElements = paginatedItems.length;
    var columns = [
        {
            title: t('driveItemDetails.dailyTimelineTable.when'),
            isVisible: true,
            className: 'sm:px-4'
        },
        {
            title: t('driveItemDetails.dailyTimelineTable.action'),
            isVisible: true,
            className: 'sm:px-4'
        },
        {
            title: t('driveItemDetails.dailyTimelineTable.by'),
            isVisible: true,
            className: 'sm:px-4'
        },
        {
            title: t('driveItemDetails.dailyTimelineTable.team'),
            isVisible: true,
            className: 'sm:px-4'
        }
    ];
    var data = paginatedItems.map(function (item) {
        var _a;
        var user = (_a = item.users) === null || _a === void 0 ? void 0 : _a[0];
        var legendKey = getDriveDailyTimelineLegendKey(item.action, item.subAction);
        return [
            {
                id: "when-".concat(item.id),
                className: 'sm:px-4',
                render: function () { return (_jsx("span", __assign({ className: "text-sm text-gray-700" }, { children: format(item.startDate, 'd MMM yyyy - HH:mm') }))); }
            },
            {
                id: "action-".concat(item.id),
                className: 'sm:px-4',
                render: function () { return (_jsxs("div", __assign({ className: "flex items-center gap-2" }, { children: [legendKey && (_jsx(DriveDocumentEventsColorSwatch, { legendKey: legendKey, className: "h-3 w-3" })), _jsx("span", __assign({ className: "text-sm text-gray-700" }, { children: getDriveTimelineItemActionLabel(item, t) }))] }))); }
            },
            {
                id: "by-".concat(item.id),
                className: 'sm:px-4',
                render: function () {
                    return user ? (_jsxs("div", __assign({ className: "flex items-center gap-2" }, { children: [_jsx(UserAvatar, { user: user, size: "small" }), _jsx("span", __assign({ className: "text-sm text-gray-900" }, { children: getDriveTimelineItemUserLabel(item) }))] }))) : (_jsx("span", __assign({ className: "text-sm text-gray-500" }, { children: "\u2014" })));
                }
            },
            {
                id: "team-".concat(item.id),
                className: 'sm:px-4',
                render: function () {
                    var _a;
                    return (_jsx("span", __assign({ className: "text-sm text-gray-700" }, { children: (_a = item.orgUnitTitle) !== null && _a !== void 0 ? _a : '—' })));
                }
            }
        ];
    });
    var table = (_jsx(Table, { columns: columns, data: data, striped: true, embedded: true, tableClassName: "min-w-[720px]" }));
    return (_jsxs("div", __assign({ className: wrapperClassName }, { children: [variant === 'bordered' ? (_jsx("div", __assign({ className: classNames('overflow-hidden rounded-lg border border-gray-200 bg-white shadow-tremor-card') }, { children: table }))) : (table), total > pageSize && (_jsxs("nav", __assign({ className: "flex items-center justify-between py-3", "aria-label": "Pagination" }, { children: [_jsx("div", __assign({ className: "hidden sm:block pt-2" }, { children: displayElements > 0 && (_jsxs("span", __assign({ className: "text-sm text-gray-700" }, { children: [t('pagination.showing'), ' ', _jsx("span", __assign({ className: "font-medium" }, { children: startIndex + 1 })), ' ', t('pagination.to'), ' ', _jsx("span", __assign({ className: "font-medium" }, { children: startIndex + displayElements })), ' ', t('pagination.of'), ' ', _jsx("span", __assign({ className: "font-medium" }, { children: total })), ' ', _jsx("span", __assign({ className: "lowercase" }, { children: t('driveItemDetails.dailyTimelineTable.events') }))] }))) })), _jsxs("div", __assign({ className: "flex justify-between sm:justify-end" }, { children: [currentPage > 1 ? (_jsx(Button, __assign({ variant: "white", onClick: function () { return onPageChange(currentPage - 1); } }, { children: t('pagination.previous') }))) : null, currentPage * pageSize < total ? (_jsx(Button, __assign({ variant: "white", onClick: function () { return onPageChange(currentPage + 1); }, className: "ml-3" }, { children: t('pagination.next') }))) : null] }))] })))] })));
};
